import { useEffect, useRef, useState } from 'react';
import type { QueueSignals } from './lib/useQueueSignals';

/**
 * Operations-panel region tabs (#65/#183 wave 3b, spec §2): the protected
 * Alerts/Assists queues stay the DEFAULT face of the queue region, and the
 * "News & Social" tab sits BEHIND them — it never displaces their reserved
 * space, it shares it on operator demand.
 *
 * The non-negotiable guard: while a panel is open the COP hides its own queue
 * (the two-queue guard), so the panel is the alerts' ONLY home — flipping to
 * News must never let an update go unseen. Same answer as the StackedQueueTabs
 * narrow-width fallback: both faces stay MOUNTED (the queues keep polling +
 * their scroll position), and a queue update while News is up BLINKS the
 * hidden Queues tab (URGENT-RED treatment, cleared on return). News gets no
 * blink — it is ambient by doctrine (PART IX) and never competes for
 * attention with the act register.
 */

export type PanelRegionTab = 'queues' | 'news';

export function PanelRegionTabs({
  queues,
  news,
  signals,
}: {
  queues: JSX.Element;
  news: JSX.Element;
  signals: QueueSignals;
}): JSX.Element {
  const [active, setActive] = useState<PanelRegionTab>('queues');
  const [queuesBlink, setQueuesBlink] = useState(false);
  // Snapshot the counters; a change while News is up marks an unseen update.
  const lastSignals = useRef<QueueSignals>(signals);
  const activeRef = useRef<PanelRegionTab>(active);
  activeRef.current = active;

  useEffect(() => {
    const changed =
      signals.alert !== lastSignals.current.alert ||
      signals.assist !== lastSignals.current.assist;
    lastSignals.current = signals;
    if (changed && activeRef.current === 'news') setQueuesBlink(true);
  }, [signals]);

  const select = (tab: PanelRegionTab): void => {
    setActive(tab);
    if (tab === 'queues') setQueuesBlink(false);
  };

  return (
    <div
      data-testid="panel-region-tabs"
      data-active-region={active}
      className="flex h-full min-h-0 flex-col"
    >
      <div role="tablist" className="flex shrink-0 gap-1 pb-2">
        <RegionTabButton
          label="Queues"
          active={active === 'queues'}
          blinking={queuesBlink}
          testid="region-tab-queues"
          onClick={() => select('queues')}
        />
        <RegionTabButton
          label="News & Social"
          active={active === 'news'}
          blinking={false}
          testid="region-tab-news"
          onClick={() => select('news')}
        />
      </div>
      {/* Both faces stay mounted — the hidden queues keep polling and their
          scroll position; only visibility flips. */}
      <div className={active === 'queues' ? 'min-h-0 flex-1' : 'hidden'}>{queues}</div>
      <div
        className={
          active === 'news'
            ? 'min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-700'
            : 'hidden'
        }
      >
        {news}
      </div>
    </div>
  );
}

function RegionTabButton({
  label,
  active,
  blinking,
  testid,
  onClick,
}: {
  label: string;
  active: boolean;
  blinking: boolean;
  testid: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={testid}
      data-blinking={blinking ? 'true' : undefined}
      onClick={onClick}
      className={`flex items-center gap-1.5 rounded px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${
        active
          ? 'bg-slate-800 text-slate-100'
          : 'text-slate-400 hover:text-slate-200'
      } ${blinking ? 'panel-tab-blink-alert' : ''}`}
    >
      <span>{label}</span>
      {blinking && (
        <span
          aria-hidden
          data-testid={`${testid}-dot`}
          className="h-2 w-2 rounded-full bg-rose-400"
        />
      )}
    </button>
  );
}
